import React, { useEffect, useReducer } from 'react';
import { usePost, useGet } from 'http/useApi';
import { rq, rqPost } from 'http/apiRoutes';
import useWindowsDimension from 'functions/useWindowsDimension';
import {
  Grid,
  Box,
  List,
  ListItemButton,
  Button,
  Typography,
  Checkbox,
  ListItemText,
  Select,
  MenuItem,
} from '@mui/material';
import FeedRoundedIcon from '@mui/icons-material/FeedRounded';
import { useAtom } from 'jotai';
import userAtom from 'data/atoms/userAtom';
import { useTranslation } from 'react-i18next';
import { SearchFilter } from 'components/ui/SearchFilter';

import _ from 'lodash';

import {
  ICompanyViewModel,
  IDepartmentViewModel,
  ThirdPartEnum,
} from 'Interfaces/ICompaniesConfig';
import {
  CloudFactoryResourceAddListViewModel,
  CloudFactoryResourceAddViewModel,
} from './Converts/ICloudFactoryInterfaces';
import { IResourcesViewModel } from 'Interfaces/IResourcesConfig';

export interface IImportResourcesInterface {
  resources: CloudFactoryResourceAddViewModel[];
  selectedResources: string[];
  resourceTypes: IResourcesViewModel[];
  resourceTypeMapping: { [email: string]: string };
}

const initialState: IImportResourcesInterface = {
  resources: [],
  selectedResources: [],
  resourceTypes: [],
  resourceTypeMapping: {},
};

type Action =
  | { type: 'RESET_FORM' }
  | { type: 'SET_RESOURCE_TYPES'; payload: IResourcesViewModel[] }
  | { type: 'SET_RESOURCES'; payload: CloudFactoryResourceAddViewModel[] }
  | { type: 'TOGGLE_RESOURCE_SELECTION'; payload: string }
  | {
      type: 'UPDATE_RESOURCE_TYPE';
      payload: { email: string; resourceTypeId: string };
    };

const reducer = (
  state: IImportResourcesInterface,
  action: Action
): IImportResourcesInterface => {
  switch (action.type) {
    case 'RESET_FORM':
      return { ...initialState };

    case 'SET_RESOURCE_TYPES': {
      const types = action.payload;
      // initialize mapping for current resources to blank
      const mapping = { ...state.resourceTypeMapping };
      state.resources.forEach((r) => {
        if (!mapping[r.email]) {
          mapping[r.email] = '';
        }
      });
      return { ...state, resourceTypes: types, resourceTypeMapping: mapping };
    }

    case 'SET_RESOURCES': {
      const resources = action.payload;
      // initialize mapping for new resources to blank
      const mapping = { ...state.resourceTypeMapping };
      resources.forEach((r) => {
        if (!mapping[r.email]) {
          mapping[r.email] = '';
        }
      });
      return { ...state, resources, resourceTypeMapping: mapping };
    }

    case 'TOGGLE_RESOURCE_SELECTION':
      return {
        ...state,
        selectedResources: state.selectedResources.includes(action.payload)
          ? state.selectedResources.filter((e) => e !== action.payload)
          : [...state.selectedResources, action.payload],
      };

    case 'UPDATE_RESOURCE_TYPE':
      return {
        ...state,
        resourceTypeMapping: {
          ...state.resourceTypeMapping,
          [action.payload.email]: action.payload.resourceTypeId,
        },
      };

    default:
      return state;
  }
};

type Props = {
  expanded: boolean;
  employees: CloudFactoryResourceAddViewModel[];
  company: ICompanyViewModel | null;
  departments: IDepartmentViewModel[];
  handleRemoveResources: (
    resources: CloudFactoryResourceAddViewModel[]
  ) => void;
};

export const ImportResourcesList = ({
  expanded,
  employees,
  departments,
  company,
  handleRemoveResources,
}: Props) => {
  const { t } = useTranslation();
  const { height } = useWindowsDimension();
  const [state, dispatch] = useReducer(reducer, initialState);
  const [user] = useAtom(userAtom);
  const listHeight = height - 238;

  const postResource = usePost(
    rqPost.importResources(company?.id || '', true),
    
  );
  const { data: resourceTypesData } = useGet(
    rq.resourceTypes(!!company?.id),
    
  );

  useEffect(() => {
    // Nullstill alltid ved endring av input eller når drawer åpnes/lukkes
    dispatch({ type: 'RESET_FORM' });

    // Hvis drawer ikke er åpen/aktiv, stopp her
    if (!expanded) return;

    // Sett ressurstyper først hvis vi har de
    if (resourceTypesData) {
      const types = _.cloneDeep(resourceTypesData.resourceTypes);
      dispatch({ type: 'SET_RESOURCE_TYPES', payload: types });
    }

    // Sett resources når vi har liste
    if (employees && employees.length > 0) {
      dispatch({
        type: 'SET_RESOURCES',
        payload: _.cloneDeep(employees),
      });
    }
  }, [employees, resourceTypesData, expanded]);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!company?.id) return;
    const list: CloudFactoryResourceAddListViewModel = {
      numberOfCloudFactoryResources: 0,
      cloudFactoryResources: [],
    };

    state.selectedResources.forEach((email) => {
      const res = state.resources.find((r) => r.email === email);
      if (res) {
        list.cloudFactoryResources.push({
          name: res.email.split('@')[0],
          creatorId: user.id,
          companyId: company.id,
          contactId: null,
          email: res.email,
          resourceTypeId: state.resourceTypeMapping[email],
          departmentId: departments[0]?.id || null,
          legacyId: res.legacyId,
          thirdPartyId: res.thirdPartyId,
          thirdParty: ThirdPartEnum.CloudFactory,
          isEmailFoundInBluu: res.isEmailFoundInBluu || false,
          isNotEmailInCloudFactory: res.isNotEmailInCloudFactory || false,
        } as CloudFactoryResourceAddViewModel);
      }
    });

    list.numberOfCloudFactoryResources = list.cloudFactoryResources.length;
    postResource.mutate(list, {
      onSuccess: (res: any) => handleRemoveResources(res.resources || []),
      onError: () => {},
    });
  };

  return (
    <Grid container spacing={0}>
      <Grid item xs={12}>
        <SearchFilter
          Icon={FeedRoundedIcon}
          Header={t('modules.cloudFactory.ResourcesHeader')}
        />
      </Grid>
      <List
        sx={{
          width: '100%',
          height: listHeight,
          p: 1,
          borderRadius: 0,
          overflow: 'auto',
          backgroundColor: 'primary.main',
        }}
      >
        {state.resources.map((resource) => (
          <ListItemButton
            key={resource.email}
            onClick={() =>
              dispatch({
                type: 'TOGGLE_RESOURCE_SELECTION',
                payload: resource.email,
              })
            }
            sx={{
              display: 'flex',
              alignItems: 'center',
              backgroundColor: state.selectedResources.includes(resource.email)
                ? 'success.dark'
                : 'primary.dark',
              borderRadius: 1,
              mb: 1,
            }}
          >
            <Box sx={{ flex: 1 }}>
              <ListItemText
                primary={resource.name}
                secondary={resource.email}
                primaryTypographyProps={{ color: 'white' }}
                secondaryTypographyProps={{ color: 'white' }}
              />
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                mx: 2,
              }}
            >
              <Typography
                sx={{ fontWeight: 'bold', fontSize: 14, color: 'primary.text' }}
              >
                {t('resources.resourceType').toUpperCase()}
              </Typography>
              <Select
                size='small'
                variant='standard'
                value={state.resourceTypeMapping[resource.email]}
                onChange={(e) =>
                  dispatch({
                    type: 'UPDATE_RESOURCE_TYPE',
                    payload: {
                      email: resource.email,
                      resourceTypeId: e.target.value as string,
                    },
                  })
                }
                displayEmpty
                sx={{ minWidth: 170, color: 'white' }}
              >
                <MenuItem value='' disabled>
                  {t('modules.cloudFactory.selectType') || 'Velg ressurstype'}
                </MenuItem>
                {state.resourceTypes.map((rt) => (
                  <MenuItem key={rt.id} value={rt.id}>
                    {rt.name}
                  </MenuItem>
                ))}
              </Select>
            </Box>
            <Checkbox
              checked={state.selectedResources.includes(resource.email)}
              color={
                state.selectedResources.includes(resource.email)
                  ? 'secondary'
                  : 'primary'
              }
              onChange={() =>
                dispatch({
                  type: 'TOGGLE_RESOURCE_SELECTION',
                  payload: resource.email,
                })
              }
            />
          </ListItemButton>
        ))}
      </List>
      <Grid item xs={12} sx={{ p: 1, backgroundColor: 'primary.main' }}>
        <Button onClick={onSubmit} variant='contained'>
          {postResource.isLoading ? t('button.creating') : t('button.create')}
        </Button>
      </Grid>
    </Grid>
  );
};
